import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { IntegrationConnection } from "@adva/types";

interface IntegrationsResponse {
  connections: IntegrationConnection[];
}

interface IntegrationStatusResponse {
  available: boolean;
  connection: IntegrationConnection | null;
}

interface ConnectResponse {
  auth_url: string;
}

interface SyncResponse {
  message: string;
  connection_id: string;
}

export function useIntegrations() {
  return useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.get<IntegrationsResponse>("/integrations"),
    select: (d) => d.connections,
  });
}

export function useIntegrationStatus(source: string) {
  return useQuery({
    queryKey: ["integrations", source, "status"],
    queryFn: () =>
      api.get<IntegrationStatusResponse>(`/integrations/${source}/status`),
  });
}

export function useConnectIntegration() {
  return useMutation({
    mutationFn: (source: string) =>
      api.post<ConnectResponse>(`/integrations/${source}/connect`, {}),
  });
}

export function useDisconnectIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (source: string) =>
      api.delete<{ success: boolean }>(`/integrations/${source}/disconnect`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}

export function useSyncIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (source: string) =>
      api.post<SyncResponse>(`/integrations/${source}/sync`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });
}
